import React, {useState} from 'react'
import {motion} from 'framer-motion'
import {useFamily} from '../../contexts/FamilyContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import Badge from '../ui/Badge'
import Card from '../ui/Card'
import ProgressBar from '../ui/ProgressBar'

const {FiEdit3, FiTrash2, FiMoreVertical, FiStar, FiTrophy} = FiIcons

const ChildCard = ({child, onEdit, onDelete, showActions = true}) => {
  const {getChildPoints, getChildLevel, completions} = useFamily()
  const [showActionsMenu, setShowActionsMenu] = useState(false)

  const points = getChildPoints(child.id)
  const level = getChildLevel(points)

  // Get today's completions for this child
  const todayCompletions = completions.filter(c => {
    const today = new Date().toDateString()
    return c.child_id === child.id && new Date(c.completed_at).toDateString() === today
  })

  // Get total completions for this child
  const totalCompletions = completions.filter(c => c.child_id === child.id)

  return (
    <Card className="p-4 hover:shadow-xl transition-all duration-300 relative">
      {/* Actions Menu - Parent View Only */}
      {showActions && (
        <div className="absolute top-4 right-4">
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowActionsMenu(!showActionsMenu)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <SafeIcon icon={FiMoreVertical} className="text-gray-500 dark:text-gray-400" />
            </Button>

            {showActionsMenu && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                className="absolute top-full right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-10 min-w-[120px]"
              >
                <button
                  onClick={() => {
                    onEdit?.(child)
                    setShowActionsMenu(false)
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <SafeIcon icon={FiEdit3} className="text-xs text-blue-500 dark:text-blue-400" />
                  Edit Profile
                </button>
                <button
                  onClick={() => {
                    onDelete?.(child)
                    setShowActionsMenu(false)
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                >
                  <SafeIcon icon={FiTrash2} className="text-xs text-red-500 dark:text-red-400" />
                  Remove Child
                </button>
              </motion.div>
            )}
          </div>
        </div>
      )}

      <div className="text-center">
        {/* Avatar */}
        <div className="text-6xl mb-3">{child.avatar || '👦'}</div>

        {/* Name and Age */}
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-lg mb-1">{child.name}</h3>
        {child.age && (
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">Age {child.age}</p>
        )}

        {/* Level Badge */}
        <div className="mb-4">
          <Badge variant="primary" className="flex items-center gap-1 mx-auto w-fit">
            <SafeIcon icon={FiStar} className="text-sm" />
            Level {level.level}: {level.title}
          </Badge>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <ProgressBar
            value={points}
            max={level.next}
            color="primary"
            size="md"
            showLabel={true}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {level.next - points} points to Level {level.level + 1}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="text-center">
            <div className="text-xl font-bold text-primary-600 dark:text-primary-400">{points}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Total Points</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-success-600 dark:text-success-400">{todayCompletions.length}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Today</div>
          </div>
        </div>

        {/* Achievement Summary */}
        <div className="p-3 bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-lg">
          <div className="flex items-center justify-center gap-2 mb-1">
            <SafeIcon icon={FiTrophy} className="text-yellow-500 dark:text-yellow-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {totalCompletions.length} Tasks Completed
            </span>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {todayCompletions.length > 0 
              ? `🔥 ${todayCompletions.length} completed today!`
              : 'Ready for the next challenge!'
            }
          </p>
        </div>
      </div>
    </Card>
  )
}

export default ChildCard